import { Trash2, ChevronsUpDown, Copy, Link2, Layers, Loader2, MoreHorizontal, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AssigneeSelector from "@/components/shared/AssigneeSelector";
import AssigneeStack from "@/components/shared/AssigneeStack";
import AssignmentSummary from "@/components/shared/AssignmentSummary";
import QuickCommentPopover from "@/components/backlog/QuickCommentPopover";
import { PRIVATE_BADGE_TIP } from "@/lib/ticketVisibility";
import { cursorTip, CursorPanel, hideCursorTip } from "@/components/shared/CursorTip";
import { supabase } from "@/integrations/db/client";
import type { TablesUpdate } from "@/integrations/db/types.generated";
import { format } from "date-fns";
import { useState, useEffect, useRef, useMemo, memo, type CSSProperties } from "react";
import PipelineProgressBadge from "@/components/pipelines/PipelineProgressBadge";
import { orderByPipeline, type Pipeline } from "@/lib/pipelines";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { getDisplayName, getUserInitials } from "@/lib/userUtils";
import ReasonRequiredDialog from "./ReasonRequiredDialog";
import { useInvalidateBacklogItems } from "@/hooks/useBacklogItems";
import { useDebouncedSave } from "@/hooks/useDebouncedSave";
import { useQueryClient } from "@tanstack/react-query";
import RichContentRenderer from "./RichContentRenderer";
import { parsePgArray, filterValidUuids } from "@/lib/pgArray";
import { orderedAssignees } from "@/lib/backlogUtils";
import { resolveAvatarUrl } from "@/lib/avatarUtils";

import { borderColorForBg } from "@/lib/constants";
import { OptionPicker, PriorityFlag, StatusPill, TypeChip } from "@/components/shared/PropertyControls";
import { requiresReason, statusKindLookup } from "@/lib/statusKind";
import type { BacklogItem, DropdownOption, UserData } from "@/types/backlog";
import { FieldsCell } from "@/components/customFields/FieldListColumns";
import type { CustomField } from "@/lib/customFields";

/**
 * Faces shown in a row's assignee stack before the rest become a "+N" chip.
 * Three is what the column's width holds without the stack spilling into the
 * task beside it; the tip on the stack names everyone regardless.
 *
 * Two below `lg`. The column is narrower there and every pixel it gives back
 * goes to Task, which on a phone is down to a handful of characters — and the
 * third face was never the one that answered "is this mine?".
 */
const MAX_STACKED_AVATARS = 3;
const MAX_STACKED_AVATARS_COMPACT = 2;

/**
 * How long the `.tick-pop` class stays on after a box becomes checked: the
 * pop's own 280ms plus a little slack, so the class never comes off mid-pop.
 * A row in a bulk cascade adds its `--tick-delay` on top (see BacklogTable).
 */
const TICK_POP_MS = 340;

/**
 * How long the cursor must rest on a truncated title before its full text
 * appears. Long enough that sweeping down the column shows nothing.
 */
const TITLE_TIP_DELAY_MS = 500;

/**
 * A ticket title as plain text, for the hover tip.
 *
 * Titles are stored as rich content, so the tip has to be told what the row is
 * showing rather than what is stored — otherwise a bolded title would hover as
 * `<p><strong>…`.
 */
const plainTitle = (task: string | null) =>
  (task ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

interface BacklogRowProps {
  item: BacklogItem;
  displayRowNumber: number;
  onUpdate: () => void;
  onDuplicate: (item: BacklogItem) => Promise<void> | void;
  onDuplicateToPlatforms: (item: BacklogItem) => void;
  openFromUrl?: boolean;
  commentIdFromUrl?: string;
  onItemOpen: () => void;
  onItemClose: () => void;
  dropdownData?: {
    statuses: DropdownOption[];
    priorities: DropdownOption[];
    ticketTypes: DropdownOption[];
    profiles: UserData[];
  };
  /** Statuses selectable in the picker for this row's platform (per-platform config).
   *  Defaults to all statuses. Badge display always uses the full set so disabled-but-in-use
   *  statuses still render. */
  selectableStatuses?: DropdownOption[];
  /** The ticket's pipeline, if any (#9304): the progress mark beside the title, and its steps lead the status picker. */
  pipeline?: Pipeline;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxChange?: (checked: boolean, shiftKey: boolean) => void;
  isAuthenticated?: boolean;
  enableContextMenu?: boolean;
  rowIndex?: number;
  /** Position within its expanded section — drives the top-to-bottom cascade. */
  revealIndex?: number;
  allowedSubsheetIds?: string[];
  hideAssignedToColumn?: boolean;
  /**
   * Whether the Status column is part of the table. It is dropped when the
   * list is grouped *by* status: the section header above the row already
   * names it, so the column repeats the answer to a question the reader just
   * asked. Every other grouping keeps it.
   */
  showStatusColumn?: boolean;
  /**
   * Whether the row is being drawn at a width the full row does not fit — set
   * by the table below `lg`, not asked for per row (see `compactRows` there).
   *
   * It buys Task its column back twice over: the assignee stack drops to two
   * faces, and the Type chip becomes the dot of its colour, with the label a
   * tap away. Both are the same trade — a label that is read once traded for
   * width in the column that is read on every row.
   */
  compact?: boolean;
  /**
   * Whether the reader is one of this ticket's assignees. Tints the row (see
   * `.row-mine`) so a person can find their own work inside a section without
   * reading a single name — the list is shared, and "which of these are mine"
   * is the question it is scanned for most.
   */
  isMine?: boolean;
  /**
   * How long a ticket may sit untouched before its Last updated cell is washed
   * amber, or null when the reader has switched that highlight off. The
   * threshold is theirs to choose (a week or a month), so it arrives as a
   * duration rather than being decided here.
   */
  staleAfterMs?: number | null;
  linkCount?: number;
  onNavigateToItem?: (itemId: string) => void;
  isLinksExpanded?: boolean;
  onToggleLinks?: (itemId: string) => void;
  /** Group-block classes for the row (see `.group-block` in index.css). */
  blockClassName?: string;
  /**
   * Identity for the list's FLIP pass (see `useAnimatedList`). The same ticket
   * is drawn twice — once in its status block, once in All tickets — so the
   * caller scopes the id per table rather than using the item's own.
   */
  flipId?: string;
  /**
   * How long this row's checkbox pop waits when the tick came from a bulk
   * selection (see `.tick-pop`) — the tick itself is instant; only the pop
   * queues. Undefined for a row the reader checked by hand, which pops at
   * once.
   */
  tickDelayMs?: number;
  /**
   * Set while the row's section is closing: its place in the bottom-to-top
   * exit run (`.row-collapse`). The table holds the row mounted for the length
   * of the run and unmounts the section afterwards.
   */
  collapseDelayMs?: number;
  /** The section's rail colour, read by `.group-block` off `--group-rail`. */
  railColor?: string;
  /**
   * A second line under the Last updated date, saying what the age MEANS on
   * the list the row is being drawn in — "blocked 52d" where the work is
   * stuck, "done 3w ago" on an archive. The backlog passes neither, so its
   * rows are exactly what they were; only the focus screens (/focus/:group)
   * ask for it, and they ask per group rather than per row.
   *
   * Two primitives rather than one object, because the row is memoised on
   * prop equality and a fresh object per render would defeat that.
   */
  agingLabel?: string;
  /** Draws the note in the softened blocked red rather than the muted grey. */
  agingUrgent?: boolean;
  /**
   * The custom Fields column (#9399, #9764), drawn after Last updated at the
   * same breakpoint when set: the fields in view that apply to this row's
   * departments, so a value whose field doesn't apply stays hidden. Memoised
   * by the table, so the row's memo still holds.
   */
  fieldsFor?: (subsheetIds: readonly string[]) => readonly CustomField[];
}

const BacklogRow = ({
  item,
  displayRowNumber,
  onUpdate,
  onDuplicate,
  onDuplicateToPlatforms,
  openFromUrl,
  commentIdFromUrl,
  onItemOpen,
  onItemClose,
  dropdownData,
  selectableStatuses,
  pipeline,
  showCheckbox = false,
  isSelected = false,
  onCheckboxChange,
  isAuthenticated = true,
  enableContextMenu = true,
  rowIndex,
  revealIndex,
  allowedSubsheetIds,
  hideAssignedToColumn = false,
  showStatusColumn = true,
  compact = false,
  isMine = false,
  staleAfterMs = null,
  linkCount,
  onNavigateToItem,
  isLinksExpanded = false,
  onToggleLinks,
  blockClassName,
  railColor,
  flipId,
  tickDelayMs,
  collapseDelayMs,
  agingLabel,
  agingUrgent = false,
  fieldsFor,
}: BacklogRowProps) => {
  const invalidateBacklogItems = useInvalidateBacklogItems();
  const queryClient = useQueryClient();

  // Edit states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editTask, setEditTask] = useState(item.task);
  const [editAssignedToIds, setEditAssignedToIds] = useState<string[]>(parsePgArray(item.assigned_to_id));
  const [editStatus, setEditStatus] = useState(item.status_id || "");
  const [editPriority, setEditPriority] = useState(item.priority_id || "");

  // Derive dropdown lists from parent-provided cache (no per-row state needed)
  const allStatuses = dropdownData?.statuses ?? [];
  const kindOf = statusKindLookup(allStatuses);
  // Picker list respects the platform's per-platform status config; falls back to all statuses.
  const pickerStatuses = useMemo(
    () => orderByPipeline(selectableStatuses ?? allStatuses, pipeline?.statusIds ?? []),
    [selectableStatuses, allStatuses, pipeline],
  );
  const allPriorities = dropdownData?.priorities ?? [];
  const allUsers = dropdownData?.profiles ?? [];

  // Derived display data — useMemo instead of useState+useEffect to avoid double-render.
  // `orderedAssignees`, not a filter over the profile list: the ticket's own
  // array order is the primary-assignee model, and a filter returns them in
  // PROFILE order, which put an arbitrary face at the head of the stack.
  const assignedToData = useMemo(
    () => orderedAssignees(item.assigned_to_id, allUsers),
    [item.assigned_to_id, allUsers],
  );

  const addedByData = useMemo(() => {
    return item.added_by_id ? (allUsers.find((p) => p.id === item.added_by_id) ?? null) : null;
  }, [item.added_by_id, allUsers]);

  const statusData = useMemo(() => {
    return item.status_id ? (allStatuses.find((s) => s.id === item.status_id) ?? null) : null;
  }, [item.status_id, allStatuses]);

  const priorityData = useMemo(() => {
    return item.priority_id ? (allPriorities.find((p) => p.id === item.priority_id) ?? null) : null;
  }, [item.priority_id, allPriorities]);

  // Used to force useEffect re-run on save error (reverts optimistic state)
  const [resetKey, setResetKey] = useState(0);

  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Remembers whether Shift was held for the click that is about to flip the
  // checkbox, so the table can extend a range instead of toggling one row.
  const shiftKeyRef = useRef(false);

  /**
   * The checkbox pop (`.tick-pop`) plays on the TRANSITION into checked, never
   * on a render that merely repeats the checked state: realtime refetches
   * re-render every row many times a minute, and list changes can remount one,
   * so a CSS-only `[data-state=checked]` animation would have already-selected
   * boxes popping at nothing. The ref starts at the mounting value, which is
   * what keeps a row that arrives selected quiet; unchecking is an erasure,
   * not an arrival, and gets no performance. The class comes off once the pop
   * (plus its place in any bulk cascade) has played, so the next check can
   * play it again.
   *
   * The transition is detected DURING RENDER, not in an effect, and that is
   * load-bearing. An effect runs after the commit, so the first committed DOM
   * was `data-state="checked"` WITHOUT `.tick-pop` — Radix mounts its
   * indicator and the declarative checked utilities paint a finished blue tick
   * for one frame. The class then arrived on the next commit, the `from`
   * keyframe took the mark back to `opacity: 0`, and the arrival played from
   * there: the mark went 1 → 0 → 1, a visible flash-then-rewind on every solo
   * click. Setting state while rendering makes React re-render this component
   * before it commits, so the class and the checked state land in the same
   * DOM update and the `from` keyframe owns the very first paint.
   */
  const [tickPop, setTickPop] = useState(false);
  const prevSelectedRef = useRef(isSelected);
  if (prevSelectedRef.current !== isSelected) {
    prevSelectedRef.current = isSelected;
    // Checking is an arrival and gets the performance; unchecking is an
    // erasure and gets none. The ref's initial value is the MOUNTING one, so a
    // row that arrives already selected is not a transition and stays quiet.
    setTickPop(isSelected);
  }
  // Read at transition time via a ref: the delay must not be a dependency, or
  // the cascade being cleared by an unrelated later click would cancel the
  // clean-up timer and strand the class on.
  const tickDelayRef = useRef(tickDelayMs);
  tickDelayRef.current = tickDelayMs;
  useEffect(() => {
    if (!tickPop) return;
    const timer = window.setTimeout(() => setTickPop(false), (tickDelayRef.current ?? 0) + TICK_POP_MS);
    return () => window.clearTimeout(timer);
  }, [tickPop]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [pendingReasonStatusId, setPendingReasonStatusId] = useState<string | null>(null);

  // Centring the status list on the ticket's current status is OptionPicker's
  // job now (it does it for every one of the three property lists), so the
  // hand-rolled ref-and-measure that used to live here is gone.

  // Handle opening from URL — navigate to full page view
  useEffect(() => {
    if (openFromUrl) {
      const commentParam = commentIdFromUrl ? `?comment=${commentIdFromUrl}` : "";
      navigate(`/item/${item.ticket_number}${commentParam}`, { replace: true });
    }
  }, [openFromUrl]);

  // Manual row ordering is gone (the list is always sorted), so the row no
  // longer participates in a drag context — this is the rail colour and
  // nothing else.
  const style: CSSProperties = railColor ? ({ "--group-rail": railColor } as CSSProperties) : {};

  /** Untouched past the reader's threshold — the Last updated cell says so. */
  const isStale = staleAfterMs !== null && Date.now() - new Date(item.updated_at).getTime() > staleAfterMs;

  useEffect(() => {
    // Skip the field currently being edited so a refetch triggered by our own
    // debounced save doesn't clobber open-popover checkbox state.
    if (editingField !== "assignedTo") {
      setEditAssignedToIds(parsePgArray(item.assigned_to_id));
    }
    setEditTask(item.task);
    setEditStatus(item.status_id || "");
    setEditPriority(item.priority_id || "");
    // editingField intentionally not in deps: re-sync only on item/resetKey change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, resetKey]);

  const handleSave = async (field: string, directValue?: any, statusReason?: string, opts?: { keepOpen?: boolean }) => {
    // Build the optimistic patch for the item
    let optimisticPatch: Partial<BacklogItem> = {};

    if (field === "assignedTo") {
      const assigneeValue = directValue !== undefined ? directValue : editAssignedToIds;
      const parsed = parsePgArray(assigneeValue);
      const sanitized = filterValidUuids(parsed);
      optimisticPatch = { assigned_to_id: sanitized.length > 0 ? sanitized : null };
    } else if (field === "task") {
      optimisticPatch = { task: directValue !== undefined ? directValue : editTask };
    } else if (field === "status") {
      const newStatusId = (directValue !== undefined ? directValue : editStatus) || null;
      const newKind = kindOf(newStatusId);
      optimisticPatch = { status_id: newStatusId };
      if (newKind === "blocked") {
        if (statusReason) optimisticPatch.blocked_reason = statusReason;
      } else {
        optimisticPatch.blocked_reason = null;
      }
      if (newKind === "rejected") {
        if (statusReason) optimisticPatch.rejected_reason = statusReason;
      } else {
        optimisticPatch.rejected_reason = null;
      }
    } else if (field === "priority") {
      optimisticPatch = { priority_id: (directValue !== undefined ? directValue : editPriority) || null };
    }

    // Optimistically update the cache immediately
    const previousItems = queryClient.getQueryData<BacklogItem[]>(["backlog-items"]);
    queryClient.setQueryData<BacklogItem[]>(["backlog-items"], (old) => {
      if (!old) return old;
      return old.map((i) => (i.id === item.id ? { ...i, ...optimisticPatch } : i));
    });

    if (!opts?.keepOpen) {
      setEditingField(null);
      setPopoverOpen(false);
    }

    try {
      // Persist to database in background
      if (field === "assignedTo") {
        const sanitized = optimisticPatch.assigned_to_id as string[] | null;
        const { error } = await supabase.rpc("update_backlog_item_assignees_v2", {
          _id: item.id,
          _assignees: sanitized && sanitized.length > 0 ? sanitized : null,
        });
        if (error) throw error;
      } else {
        let updateData: TablesUpdate<"backlog_items"> = {};
        switch (field) {
          case "task":
            updateData = { task: optimisticPatch.task as string };
            break;
          case "status":
            updateData = { status_id: optimisticPatch.status_id as string | null };
            if (optimisticPatch.blocked_reason !== undefined) {
              updateData.blocked_reason = optimisticPatch.blocked_reason as string | null;
            }
            if (optimisticPatch.rejected_reason !== undefined) {
              updateData.rejected_reason = optimisticPatch.rejected_reason as string | null;
            }
            break;
          case "priority":
            updateData = { priority_id: optimisticPatch.priority_id as string | null };
            break;
        }
        const { error } = await supabase.from("backlog_items").update(updateData).eq("id", item.id);
        if (error) throw error;
      }

      // Sync after successful save. Prefix invalidation: the detail query is
      // keyed by the URL param (usually the ticket number, not this UUID), so
      // a keyed invalidation never matched it.
      queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
      invalidateBacklogItems();
      onUpdate();
    } catch (error: any) {
      console.error("Failed to update backlog item:", error);
      const errorMessage = error?.message || error?.error_description || "Failed to save changes";
      toast.error(errorMessage);
      // Rollback optimistic update
      if (previousItems) {
        queryClient.setQueryData<BacklogItem[]>(["backlog-items"], previousItems);
      }
      // Close the editor so the resetKey re-sync isn't skipped for the open field
      setEditingField(null);
      setPopoverOpen(false);
      setResetKey((k) => k + 1);
    }
  };

  const debouncedAssigneeSave = useDebouncedSave<string[]>((ids) =>
    handleSave("assignedTo", ids, undefined, { keepOpen: true }),
  );

  const handleStatusSelect = (status: DropdownOption) => {
    if (requiresReason(status.kind)) {
      setPendingReasonStatusId(status.id);
      setShowReasonDialog(true);
    } else {
      handleSave("status", status.id);
    }
  };

  const handleReasonConfirm = (reason: string) => {
    if (pendingReasonStatusId) {
      handleSave("status", pendingReasonStatusId, reason);
    }
    setShowReasonDialog(false);
    setPendingReasonStatusId(null);
  };

  const handleReasonCancel = () => {
    setShowReasonDialog(false);
    setPendingReasonStatusId(null);
  };

  const handleClick = (field: string) => {
    // Flush the null state so React processes the close before we re-open
    flushSync(() => {
      setEditingField(null);
      setPopoverOpen(false);
    });

    // Reset to current item values
    switch (field) {
      case "task":
        setEditTask(item.task);
        break;
      case "assignedTo":
        setEditAssignedToIds(parsePgArray(item.assigned_to_id));
        break;
      case "status":
        setEditStatus(item.status_id || "");
        break;
      case "priority":
        setEditPriority(item.priority_id || "");
        break;
    }

    setEditingField(field);

    // Open popover for dropdown fields
    if (["assignedTo", "status", "priority"].includes(field)) {
      setPopoverOpen(true);
      // These open UNDER the cursor that summoned them, so the trigger's tip
      // would otherwise sit on top of the list until the pointer next moved.
      hideCursorTip();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const { error } = await supabase
        .from("backlog_items")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", item.id);

      if (error) throw error;
      invalidateBacklogItems();
      // Also invalidate deleted items query so Recently Deleted page updates
      queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
      // The hold replaced the confirmation dialog, so this is now the only
      // acknowledgement — and the only place the way back is named.
      toast.success(`#${item.ticket_number} moved to Recently deleted`);
    } catch (error) {
      console.error("Failed to soft-delete backlog item:", error);
      toast.error("Failed to delete item");
    }
  };

  const [isDuplicating, setIsDuplicating] = useState(false);

  const handleDuplicate = async () => {
    if (isDuplicating) return;
    setIsDuplicating(true);
    try {
      await onDuplicate(item);
    } finally {
      setIsDuplicating(false);
    }
  };

  // The priority trigger is the same element open or shut, so the flag does
  // not move or change size when the picker opens.
  // Ring wash, not bg-muted: on a dark card --muted is a 1% lift (and on a
  // highlighted "mine" row, none at all) — the cyan wash reads on every row
  // surface in both themes.
  const priorityTriggerClass =
    "mx-auto flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[hsl(var(--ring)/0.18)] data-[state=open]:bg-[hsl(var(--ring)/0.18)]";
  const priorityAriaLabel = priorityData ? `Priority: ${priorityData.label}` : "Set priority";
  // A shade larger than a 16px glyph: beside a column of filled status pills
  // that read as an afterthought. The colour work lives in `PriorityFlag`
  // (PropertyControls), which the new-ticket form draws from too.
  const priorityGlyph = <PriorityFlag priority={priorityData} />;

  /**
   * The Assigned cell's one control, open or shut — the face stack itself (or
   * the "-" of an unassigned row), exactly the priority flag's pattern. As the
   * display (`interactive`) it carries the names-tip and opens the picker; as
   * the popover's trigger it is the same element in the same place, so nothing
   * moves or reboxes when the picker opens. A real <button> both ways: Radix
   * needs a focusable trigger, and the display state should be one too.
   */
  const maxStackedAvatars = compact ? MAX_STACKED_AVATARS_COMPACT : MAX_STACKED_AVATARS;

  /**
   * Where the cursor entered the Assigned stack, or null when it is not over
   * it — the open flag and the anchor for the hover panel in one value.
   *
   * A cell-local piece of state rather than a `cursorTip()` data attribute
   * because what this cell has to say is not a sentence: who owns the ticket,
   * who is helping, and who put it on them. That is the panel below.
   */
  const [assignedTipAt, setAssignedTipAt] = useState<{ x: number; y: number } | null>(null);

  const assigneeStackButton = (interactive: boolean) => (
    <button
      type="button"
      aria-label={
        assignedToData.length > 0
          ? `Assigned: ${assignedToData.map((user) => getDisplayName(user)).join(", ")}`
          : "Assign someone"
      }
      onMouseEnter={
        interactive && assignedToData.length > 0 ? (e) => setAssignedTipAt({ x: e.clientX, y: e.clientY }) : undefined
      }
      onMouseLeave={interactive ? () => setAssignedTipAt(null) : undefined}
      // `-m-2 p-2` is HEADROOM, not spacing: the negative margin gives back
      // exactly what the padding takes, so the layout box is unchanged while
      // the CLIPPING box grows 8px at every edge. `overflow-hidden` is here to
      // truncate a long assignee row, and it was doing so at exactly the
      // avatars' own box. CSS cannot clip one axis and not the other (a
      // `visible` paired with a `hidden` computes to `auto`, i.e. a
      // scrollport), so the room has to come from the box itself.
      //
      // EIGHT, not four: the primary's star is a badge hanging 4px off the
      // face's top-LEFT corner, and the hover lift carries it 2px further —
      // 6px past the box on two edges at once, so the corner of the mark that
      // says "this one owns it" was shaved off exactly when the reader pointed
      // at it.
      //
      // And `w-[calc(100%+1rem)]`, NOT `w-full` — this is the half that took
      // two goes. Preflight sets `box-sizing: border-box`, so `w-full` is a
      // BORDER-box width: the padding is taken out of the width rather than
      // added to it, and the negative margin then merely SLID the same-sized
      // box 8px left. The clip box never grew, the content box actually
      // SHRANK by 16px, and the stack lost its right end — the "+N" chip that
      // says how many people are not shown — to a box narrower than the cell
      // it sits in. Adding the 16px back makes the padding real headroom on
      // both edges and hands the content box the cell's full width.
      className="-m-2 flex w-[calc(100%+1rem)] min-w-0 cursor-pointer items-center overflow-hidden p-2 text-left"
      onClick={(e) => {
        // The row beneath opens the ticket on a single click; this cell's
        // click is a different verb and must not do both.
        e.stopPropagation();
        setAssignedTipAt(null);
        if (interactive && editingField !== "assignedTo") handleClick("assignedTo");
      }}
    >
      {assignedToData.length > 0 ? (
        // Capped at three (two below `lg`). A ticket with five people on it
        // used to push its own stack through the next column; the count chip
        // is a fixed width whatever the real number is, and the panel above
        // names every one of them. The primary leads and carries the star —
        // see AssigneeStack, which also owns the reversed layering that keeps
        // the leading face on top of the ones behind it.
        <AssigneeStack people={assignedToData} max={maxStackedAvatars} />
      ) : (
        <span className="text-muted-foreground">-</span>
      )}
    </button>
  );

  /**
   * The Assigned cell's hover panel — the plain names-list tip that used to
   * live here, rebuilt as the thing a reader actually wants off this column.
   *
   * It is drawn as the ticket page's own assignment strip (AssignmentSummary),
   * so the tip reads as a preview of the page one click away rather than as a
   * second, differently-shaped answer.
   *
   * The second line is WHO PUT IT ON THEM, and it says "Added by" because that
   * is the fact the row actually carries. There is no per-row assignment actor
   * to be had cheaply: nothing writes an `assignee_change` activity row (the
   * log is only written for comments and attachments), and
   * `assignment_notifications.assigned_by_id` — the one column that does
   * record an actor — holds a row per NOTIFIED PERSON, skips self-assignment
   * entirely, and is never written for seeded or service-role changes. Reading
   * it would be a second query over the whole list for an answer that is
   * missing on most rows. The filer is the honest nearest thing, so it is
   * labelled as the filer.
   */
  const assignedPanel = (
    <CursorPanel open={assignedTipAt !== null} at={assignedTipAt} onDismiss={() => setAssignedTipAt(null)}>
      <div className="flex min-w-[13rem] flex-col gap-2">
        {/* Helpers as a LIST, not a second stack: the panel is opened to find
            out who these faces are, and a row of faces answers that with the
            same faces. */}
        <AssignmentSummary people={assignedToData} helperLayout="list" />
        {addedByData && (
          <div className="flex items-center gap-2 border-t border-border pt-2 text-[11.5px]">
            <Avatar className="h-5 w-5 shrink-0">
              {addedByData.avatar_url && <AvatarImage src={resolveAvatarUrl(addedByData.avatar_url)} alt="" />}
              <AvatarFallback className="avatar-plate text-[9px] font-medium text-primary dark:text-blue-200">
                {getUserInitials(addedByData)}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0 truncate">
              Added by <b className="font-semibold text-foreground">{getDisplayName(addedByData)}</b>
            </span>
          </div>
        )}
      </div>
    </CursorPanel>
  );

  const rowElement = (
    <tr
      data-flip-id={flipId}
      // A section that has just been expanded reveals its rows top-to-bottom;
      // the delay is capped so a 25-row group doesn't take a second to appear.
      // A CLOSING section's delay wins over the reveal's: `.row-collapse` is
      // the animation actually playing then, and it reads the same property.
      style={
        collapseDelayMs !== undefined
          ? { ...style, animationDelay: `${collapseDelayMs}ms` }
          : revealIndex === undefined
            ? style
            : { ...style, animationDelay: `${30 + Math.min(revealIndex, 8) * 40}ms` }
      }
      className={cn(
        // The table is `border-separate`, where a border on <tr> never paints —
        // the hairline separator has to live on the cells (TAKT-style faint
        // rules instead of zebra striping, which reads as noise beside them).
        // `table-row-hover` (index.css) is the designed row-hover token —
        // hover:bg-muted would be invisible, since --muted equals --background.
        // The row sits on --card, not --background: the sections are cards on
        // the page now, and the page is the grey behind them.
        "group relative cursor-pointer transition-colors table-row-hover",
        // One surface class, never both: `.row-mine` and `bg-card` are the
        // same specificity in the same layer, so which one wins would come
        // down to file order rather than intent. Whichever is applied also
        // publishes `--row-bg` for anything sitting on the row (see
        // `.avatar-ring`) to match against.
        isMine ? "row-mine" : "bg-card",
        revealIndex !== undefined && "cascade-item",
        // Closing section: the bottom-to-top exit. Declared after
        // `.cascade-item` in index.css, so its animation is the one that plays.
        collapseDelayMs !== undefined && "row-collapse",
        // Left rail, right edge and corner radii of the section's block.
        blockClassName,
        // Hairline grid: a rule under every row, and one between every pair of
        // *columns the reader thinks of as columns*. The narrow utility cells
        // (drag handle, link count) are not columns — they belong to the
        // checkbox and the ID beside them — so the cell each pair starts with
        // opts out with `data-vrule="off"` and the pair emits a single rule.
        // Same for the sticky action cell, which already carries `border-l`.
        "[&>td]:border-b [&>td]:border-[hsl(var(--table-hairline))] [&>td:not([data-vrule=off])]:border-r",
      )}
      onDoubleClick={() => navigate(`/item/${item.ticket_number}`)}
    >
      {showCheckbox && (
        <td
          // Padding matched to the header's select-all (see
          // renderColumnHeaderRow), and SYMMETRIC — which is the fix, not the
          // bug. This is the block's first cell, so it carries the 3px coloured
          // rail as its `border-left` and the column rule as its `border-right`.
          // Those are borders, so the content box already starts at the rail's
          // INNER edge: equal padding centres the box between the two things
          // the eye actually reads as the cell's walls. The previous asymmetric
          // pair was compensating for the rail a second time and left the box
          // 11px from the rail against 13px from the rule — visibly left of
          // centre. 44px cell − 3px rail − 1px rule − 16px box = 12px a side.
          className="w-11 py-3 px-2 text-center"
          onClick={(e) => e.stopPropagation()}
          onClickCapture={(e) => {
            shiftKeyRef.current = e.shiftKey;
          }}
        >
          <div className="flex items-center justify-center">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onCheckboxChange?.(checked === true, shiftKeyRef.current)}
              className={tickPop ? "tick-pop" : undefined}
              style={tickPop ? ({ "--tick-delay": `${tickDelayMs ?? 0}ms` } as CSSProperties) : undefined}
            />
          </div>
        </td>
      )}
      {/* No drag handle: rows are not hand-ordered any more. The column it
          needed — admin-only, empty for everyone else, 2rem of every row —
          went with it. */}
      {/* The ID owns its own rule. It used to hand it to the link-count cell
          beside it, which made the two read as one column — and left the
          number sitting visibly left of that column's centre. */}
      {/* A single click here opens the ticket (so does the Task cell). The
          editable cells own their clicks and stop them, so "click the ticket's
          name or number to read it" and "click a pill to change it" stay two
          different verbs; the double-click on the row survives for muscle
          memory but is no longer the only way in. */}
      <td
        onClick={() => navigate(`/item/${item.ticket_number}`)}
        className="w-16 px-1 py-2 text-xs text-muted-foreground text-center font-mono"
      >
        #{item.ticket_number}
      </td>

      {/* Assigned To - Editable. Same shape as Priority below: the display —
          the face stack, or the "-" of an unassigned row — IS the control,
          open or shut, and the picker hangs directly under it. It used to swap
          in AssigneeSelector's boxed combobox on click, which repainted the
          cell as a text input the reader never asked for and pushed the
          picker off the box's own geometry rather than the faces they
          clicked. */}
      {!hideAssignedToColumn && (
        <td className="w-[6.75rem] px-3 py-2 text-sm max-lg:px-2">
          {editingField === "assignedTo" ? (
            <AssigneeSelector
              assigneeIds={editAssignedToIds}
              onAssigneeIdsChange={(ids) => {
                setEditAssignedToIds(ids);
                debouncedAssigneeSave.schedule(ids);
              }}
              profiles={allUsers}
              open={popoverOpen}
              onOpenChange={(o) => {
                setPopoverOpen(o);
                if (!o) {
                  debouncedAssigneeSave.flush();
                  setEditingField(null);
                }
              }}
              contentClassName="z-[100] w-64"
            >
              {/* The trigger is the same stack the cell shows at rest —
                  AssigneeSelector renders `children` in place of its boxed
                  combobox. The hover panel is dropped while open, the way the
                  priority flag drops its label tip: the list below is already
                  the names. */}
              {assigneeStackButton(false)}
            </AssigneeSelector>
          ) : (
            <>
              {assigneeStackButton(true)}
              {assignedPanel}
            </>
          )}
        </td>
      )}

      {/* Task. Single click opens the ticket — see the ID cell above. The
          link-count and comment affordances inside stop their own clicks. */}
      <td
        onClick={() => navigate(`/item/${item.ticket_number}`)}
        className="min-w-[20rem] px-4 py-2 text-sm max-lg:px-2.5"
      >
        <div className="flex items-center gap-2">
          {/* A private ticket says so ahead of its own title: the lock is the
              reason a colleague cannot find this row at all, so it belongs
              where the eye lands first — not in the trailing trio, which is
              pinned to the right edge and spoken for by controls. Renders
              nothing for a public ticket, which is nearly every row. */}
          {item.is_private && (
            <Lock
              className="h-3.5 w-3.5 shrink-0 text-destructive"
              aria-label="Private ticket"
              {...cursorTip(PRIVATE_BADGE_TIP, "Private:")}
            />
          )}
          {/* One line, always. A wrapping title makes rows different heights,
              which turns a scannable column of tickets into a ragged list and
              costs the reader the vertical rhythm they scan by — and the
              second line is rarely the half that identifies the ticket.
              The full title is a hover away.

              The tip is DELAYED (500ms): every row has one, so an immediate
              tip would fire once per row as the pointer crosses the column and
              read as flicker. Half a second means the cursor has stopped on a
              title the reader is actually trying to read. */}
          <span
            className="text-foreground flex-1 min-w-0 overflow-hidden"
            {...cursorTip(plainTitle(item.task), undefined, TITLE_TIP_DELAY_MS)}
          >
            {/* `whitespace-nowrap` explicitly, on top of `truncate`: the
                legacy (non-HTML) branch of RichContentRenderer sets
                `whitespace-pre-wrap` on its own root, and tailwind-merge does
                not treat that as conflicting with `truncate` — so the ellipsis
                was set up and then never reached, because the text was still
                allowed to wrap. Naming the property directly is what overrides
                it. */}
            <RichContentRenderer
              content={item.task}
              className="truncate whitespace-nowrap [&_br]:hidden [&_p]:inline"
            />
          </span>
          {/* The trailing trio, pinned to the cell's right edge by `ml-auto`
              rather than by whatever the title happens to leave behind: the
              title truncates into the space that is left, so a one-word ticket
              and a forty-word one put these controls in exactly the same place
              down the column, which is what makes them findable at a glance.

              Right to left they run TYPE, comment, link — read inward from the
              edge. The type chip is the column's stable landmark (every row has
              one, and it is the facet the eye sorts by), so it takes the edge;
              the comment button is the affordance next most often reached for;
              the link chip, which most rows do not have, sits innermost where
              its coming and going cannot shift the other two. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* The two affordances that used to need columns of their own: the
                link count (which most rows don't have) and the comment button.
                Both belong to the task, so they ride at the end of its cell
                rather than reserving width on every row. */}
            {pipeline && (
              <PipelineProgressBadge
                name={pipeline.name}
                stepStatusIds={pipeline.statusIds}
                statusId={item.status_id}
              />
            )}
            {(linkCount ?? 0) > 0 && (
              <button
                type="button"
                className={cn(
                  "flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-xs transition-colors",
                  isLinksExpanded
                    ? "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400"
                    : "text-muted-foreground/70 hover:bg-[hsl(var(--ring)/0.18)] hover:text-foreground",
                )}
                title={`${linkCount} linked item(s)`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLinks?.(item.id);
                }}
              >
                <Link2 className="h-3 w-3" />
                <span>{linkCount}</span>
              </button>
            )}
            <QuickCommentPopover backlogItemId={item.id} ticketNumber={item.ticket_number} />
            {(() => {
              const ticketType =
                item.ticket_type_id && dropdownData?.ticketTypes
                  ? dropdownData.ticketTypes.find((t) => t.id === item.ticket_type_id)
                  : null;
              if (!ticketType) return null;
              // Below `lg` the chip is the colour and nothing else. "Task" and
              // "Bug" cost ~45px of a Task column that has about 70 to give at
              // 375px — and the type is a facet you filter by far more often
              // than you read it. The dot keeps the one part that scans (the
              // colour) and hands back the label on a tap, on a 28px target
              // rather than the chip's 20px one.
              if (compact) {
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Type: ${ticketType.label}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-[hsl(var(--ring)/0.18)] data-[state=open]:bg-[hsl(var(--ring)/0.18)]"
                      >
                        <span
                          aria-hidden
                          className="h-2.5 w-2.5 rounded-full border"
                          style={{
                            backgroundColor: ticketType.color,
                            borderColor: borderColorForBg(ticketType.color),
                          }}
                        />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      side="top"
                      sideOffset={6}
                      collisionPadding={12}
                      className="w-auto border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {ticketType.label}
                    </PopoverContent>
                  </Popover>
                );
              }
              return <TypeChip type={ticketType} />;
            })()}
          </div>
        </div>
      </td>

      {/* Status - Editable. Absent when the list is grouped by status: the
          block header the row sits under has already said it. */}
      {showStatusColumn && (
        <td className="w-32 px-3 py-2 text-sm max-lg:px-2">
          {editingField === "status" ? (
            /* The shared list (OptionPicker in PropertyControls), as Priority
               below already used. This was a hand-rolled copy of it drawing
               the same coloured-square rows, and the copy had drifted: no mark
               on the ticket's current status, cmdk's first row pre-highlighted
               on open, and `hover:bg-muted` for a highlight — which paints
               nothing in light mode, where --muted equals --background. The
               shared list answers all three, and carries the centre-the-
               current-option scroll this site used to do by hand. */
            <OptionPicker
              options={pickerStatuses}
              selectedId={item.status_id}
              onSelect={handleStatusSelect}
              open={popoverOpen}
              onOpenChange={(o) => {
                setPopoverOpen(o);
                if (!o) setEditingField(null);
              }}
              className="z-[100] w-full"
            >
              <Button
                variant="outline"
                role="combobox"
                className="h-8 w-full justify-between border-input bg-background text-xs text-foreground hover:bg-[hsl(var(--ring)/0.18)] hover:text-foreground data-[state=open]:bg-[hsl(var(--ring)/0.18)]"
              >
                {statusData?.label || "Select"}
                <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </OptionPicker>
          ) : (
            <div
              className="cursor-pointer text-center"
              onClick={(e) => {
                e.stopPropagation();
                if (editingField !== "status") handleClick("status");
              }}
            >
              {/* A filled pill across the cell, monday-style: status is the
                  column you scan, and a block of colour is read before any
                  word in it. `.status-pill` (index.css) is its hover — the
                  pill opens the status picker, and at rest it was
                  indistinguishable from a read-only badge. Shared with the
                  new-ticket form (see StatusPill in PropertyControls). */}
              <StatusPill status={statusData} className="w-full" />
            </div>
          )}
        </td>
      )}

      {/* Priority - Editable. The trigger is the flag alone, open or shut: a
          combobox box with a word in it did not fit a 5rem column, and the
          text spilled over the neighbouring cell the moment it opened. The
          list below still carries the labels and their colours. */}
      <td className="w-[5.5rem] px-2 py-2 text-sm max-lg:px-1">
        {editingField === "priority" ? (
          /* The shared list (OptionPicker in PropertyControls), not a copy of
             it: the row it opens on is the ticket's current priority, and that
             mark has to be the same one the new-ticket form draws. Its own
             narrower width and the row's stacking context are all this site
             still sets. */
          <OptionPicker
            options={allPriorities}
            selectedId={item.priority_id}
            onSelect={(priority) => {
              handleSave("priority", priority.id);
            }}
            open={popoverOpen}
            onOpenChange={(o) => {
              setPopoverOpen(o);
              if (!o) setEditingField(null);
            }}
            className="z-[100] w-44"
          >
            <button type="button" className={priorityTriggerClass} aria-label={priorityAriaLabel}>
              {priorityGlyph}
            </button>
          </OptionPicker>
        ) : (
          <button
            type="button"
            className={priorityTriggerClass}
            aria-label={priorityAriaLabel}
            onClick={(e) => {
              e.stopPropagation();
              if (editingField !== "priority") handleClick("priority");
            }}
            // The flag is the whole cell, so the tip is the only place the rank
            // is spelled out — same wording as the new-ticket form's, which is
            // the other place this control appears.
            {...cursorTip(priorityData ? priorityData.label : "Choose a priority", "Priority:")}
          >
            {priorityGlyph}
          </button>
        )}
      </td>

      {/* Added By - NOT Editable */}
      <td className="w-40 px-4 py-2 text-sm hidden 2xl:table-cell">
        {/* `min-w-0` + `truncate`: the column gave 0.5rem of its width to the
            date columns, and an un-clipped name is the one thing in here that
            has no natural ceiling. */}
        <div className="flex min-w-0 items-center gap-2">
          {addedByData ? (
            <>
              <Avatar className="h-6 w-6 shrink-0">
                {addedByData.avatar_url && <AvatarImage src={resolveAvatarUrl(addedByData.avatar_url)} alt="Avatar" />}
                <AvatarFallback className="avatar-plate text-xs text-primary dark:text-blue-200 font-medium">
                  {getUserInitials(addedByData)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-foreground">{getDisplayName(addedByData)}</span>
            </>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      </td>

      {/* Created - NOT Editable */}
      <td className="hidden w-28 whitespace-nowrap px-2 py-2 text-center text-sm text-muted-foreground xl:table-cell">
        {format(new Date(item.created_at), "MMM d, yyyy")}
      </td>

      {/* Last updated - NOT Editable. Filed beside Created because the pair is
          one question ("how old is this, and is anyone on it?") — a date on its
          own says nothing about whether the ticket is moving.

          Over a week untouched and the cell washes amber. The wash is on the
          CELL, not the row: it is a fact about this column, and a whole row
          going yellow would out-shout the status pill that actually says what
          the ticket is doing. */}
      <td
        className={cn(
          "hidden w-32 whitespace-nowrap px-2 py-2 text-center text-sm text-muted-foreground xl:table-cell",
          isStale && "cell-stale",
        )}
        title={isStale ? "No activity since this date" : undefined}
      >
        {format(new Date(item.updated_at), "MMM d, yyyy")}
        {/* The context line, when the list drawing this row has one to add
            (see `agingLabel`). A second line rather than a column of its own:
            it is the same fact this cell already states, read for what it
            means here, and a new column would move every column after it out
            of line with the backlog's. */}
        {agingLabel && (
          <span
            className={cn(
              "mt-0.5 block text-[11px] font-bold leading-none",
              agingUrgent ? "text-[hsl(var(--blocked-fg))]" : "text-muted-foreground/70",
            )}
          >
            {agingLabel}
          </span>
        )}
      </td>

      {fieldsFor && <FieldsCell fields={fieldsFor(parsePgArray(item.subsheet_ids))} values={item.custom_fields} />}

      {/* No ETA column: the date earned its width on the ticket, not in a list
          nobody sorted by it. It still reads and edits on the detail page. */}

      {/* Row actions, all of them, behind one kebab. Three ghost icons on
          hover was three chances to click the wrong one on a 32px target, and
          the column they needed cost every row 8rem of width — the menu is one
          target, and the column is now the width of it. The menu names what
          each action does, which the icons never did. */}
      <td
        data-vrule="off"
        // No longer sticky, and no background of its own: the table fits its
        // container now, so nothing scrolls underneath this cell for it to
        // cover — and without an opaque fill it takes the row's hover like
        // every other cell instead of needing its own copy of it.
        //
        // No `border-l` either. It is a leftover from the sticky era, and the
        // table is `border-separate`: an adjacent pair of 1px borders does not
        // collapse into one, so this cell's left rule sat directly against Last
        // updated's right rule and the boundary drew at 2px while every other
        // divider drew at 1. The cell on the left already emits the rule.
        //
        // Symmetric padding, and the kebab CENTRED in it. It used to be ranged
        // right against an asymmetric `pr-3`, which happened to land dead-centre
        // at exactly 56px wide and nowhere else — so the moment the column was
        // any wider than its class said (see `compactMinWidth` in BacklogTable:
        // a fixed table with no auto column grew this one to 100px) the dots
        // slid to the right wall. Centred in a symmetric box, the glyph sits on
        // the column's middle whatever the column's width turns out to be.
        className="w-14 px-2 py-2 max-lg:px-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                title="Row actions"
                aria-label="Row actions"
                className={cn(
                  // Always on screen, quiet at rest. Hiding it until row hover
                  // made every row's one action undiscoverable — nothing said
                  // the column was anything but empty space, and a control you
                  // have to find by sweeping the cursor over it is a control
                  // most people never find. What hover adds now is the button
                  // chrome (the ghost fill), which is the part that says
                  // "this is clickable"; the dots themselves never leave.
                  "h-8 w-8 text-muted-foreground/60 transition-colors hover:bg-[hsl(var(--ring)/0.18)] hover:text-foreground",
                  "data-[state=open]:bg-[hsl(var(--ring)/0.18)] data-[state=open]:text-foreground",
                )}
              >
                {isDuplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            {/* "Insert row above/below" is gone with the drag handle: both
                were ways of placing a ticket at a position, and a list that is
                always sorted has no positions to place it at. Filing a new
                ticket is "Add task" in the section header. */}
            <DropdownMenuContent align="end" className="w-56 z-[200]">
              <DropdownMenuItem
                disabled={isDuplicating}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDuplicate();
                }}
                className="cursor-pointer"
              >
                <Copy className="mr-2 h-4 w-4" />
                <span>Duplicate row</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicateToPlatforms(item);
                }}
                className="cursor-pointer"
              >
                <Layers className="mr-2 h-4 w-4" />
                <span>Duplicate to departments…</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* The hold-to-delete that used to live in this cell is gone
                  with the icon it was drawn on: a menu item cannot be held.
                  This is the same confirmation path the context menu's
                  "Delete row" has always taken, and the delete stays
                  recoverable from Recently deleted either way. */}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  // Let the menu close before the dialog opens, or the two
                  // fight over focus.
                  setTimeout(() => handleDeleteClick(), 10);
                }}
                className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-950/50 dark:focus:text-red-300"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete row</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteConfirm}
        items={[{ id: item.id, task: item.task, ticket_number: item.ticket_number }]}
      />

      <ReasonRequiredDialog
        open={showReasonDialog}
        onOpenChange={setShowReasonDialog}
        onConfirm={handleReasonConfirm}
        onCancel={handleReasonCancel}
        title={kindOf(pendingReasonStatusId) === "rejected" ? "Rejection Reason Required" : "Blocked Reason Required"}
        description={
          kindOf(pendingReasonStatusId) === "rejected"
            ? "Please provide a reason for rejecting this item."
            : "Please provide a reason for marking this item as blocked."
        }
        placeholder={
          kindOf(pendingReasonStatusId) === "rejected"
            ? "Enter the reason this item is being rejected..."
            : "Enter the reason this item is blocked..."
        }
      />
    </tr>
  );

  if (!isAuthenticated || !enableContextMenu) {
    return rowElement;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{rowElement}</ContextMenuTrigger>
      <ContextMenuContent className="w-56 bg-card border-border z-[200]">
        <ContextMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleDuplicate();
          }}
          className="text-foreground hover:bg-muted hover:text-foreground cursor-pointer"
        >
          <Copy className="mr-2 h-4 w-4" />
          <span>Duplicate row</span>
        </ContextMenuItem>
        <ContextMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDuplicateToPlatforms(item);
          }}
          className="text-foreground hover:bg-muted hover:text-foreground cursor-pointer"
        >
          <Layers className="mr-2 h-4 w-4" />
          <span>Duplicate to departments…</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onClick={(e) => {
            e.stopPropagation();
            // Delay slightly to ensure context menu closes before dialog opens
            setTimeout(() => handleDeleteClick(), 10);
          }}
          className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-950/50 dark:focus:text-red-300"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          <span>Delete row</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default memo(
  BacklogRow,
  (prev, next) =>
    prev.item === next.item &&
    prev.displayRowNumber === next.displayRowNumber &&
    prev.openFromUrl === next.openFromUrl &&
    prev.commentIdFromUrl === next.commentIdFromUrl &&
    prev.dropdownData === next.dropdownData &&
    prev.selectableStatuses === next.selectableStatuses &&
    prev.pipeline === next.pipeline &&
    prev.showCheckbox === next.showCheckbox &&
    prev.isSelected === next.isSelected &&
    prev.isAuthenticated === next.isAuthenticated &&
    prev.enableContextMenu === next.enableContextMenu &&
    prev.rowIndex === next.rowIndex &&
    prev.revealIndex === next.revealIndex &&
    prev.allowedSubsheetIds === next.allowedSubsheetIds &&
    prev.hideAssignedToColumn === next.hideAssignedToColumn &&
    prev.showStatusColumn === next.showStatusColumn &&
    prev.compact === next.compact &&
    prev.isMine === next.isMine &&
    prev.staleAfterMs === next.staleAfterMs &&
    prev.linkCount === next.linkCount &&
    prev.isLinksExpanded === next.isLinksExpanded &&
    prev.onToggleLinks === next.onToggleLinks &&
    prev.blockClassName === next.blockClassName &&
    prev.railColor === next.railColor &&
    prev.flipId === next.flipId &&
    prev.tickDelayMs === next.tickDelayMs &&
    prev.collapseDelayMs === next.collapseDelayMs &&
    prev.agingLabel === next.agingLabel &&
    prev.agingUrgent === next.agingUrgent &&
    prev.fieldsFor === next.fieldsFor,
);
